// 🌐 React & State
import {SetStateAction, useEffect, useState} from "react";

// 🧠 State Management
import { observer } from "mobx-react-lite";
import contentManager from "../../stores/content-manager";

// 🔧 Utilities
import serializationManager from "../../stores/serialization-manager";
import prettier from "prettier/standalone";
import parserHtml from "prettier/plugins/html";
import parserCss from "prettier/plugins/postcss";

// 🎨 Syntax Highlighting (PrismJS)
import Prism from "prismjs";
import "prismjs/components/prism-markup"; // for HTML
import "prismjs/themes/prism.css";        // default theme
import "prismjs/components/prism-css";
import bodyStylesManager from "../../stores/body-styles-manager";

interface CodeEditorProps {
	editorMode: string;
}

/**
 * CodeEditor – Read-Only Source View for HTML and CSS in PageBuilder
 *
 * @component
 * @description
 * Provides a formatted, syntax-highlighted read-only view of the current HTML or CSS
 * output from the builder, based on the active editor mode ("HTML", "Styles", or "Text").
 * Uses Prettier for code formatting and PrismJS for highlighting. 
 *
 * @features
 * - Switches between HTML and CSS source view based on editorMode prop
 * - Formats code with Prettier (HTML/CSS) for improved readability
 * - Syntax highlighting via PrismJS
 * - Updates view in response to builder content or style changes
 * - Placeholder for Text mode (live editing deferred to v2.0+)
 *
 * @props
 * @param {string} editorMode – The current editor tab ("HTML", "Styles", or "Text")
 *
 * @remarks
 * - Does not yet support live code editing; all views are read-only for v1.x
 * - Intended as an internal PageBuilder component, not for standalone use
 * - Content is updated automatically when design blocks or styles change
 *
 * @example
 * <CodeEditor editorMode="HTML" />
 *
 * @since 2025-06-10
 * @lastUpdated 2025-07-15
 * @author
 * d2d-weizhi (Chen Weizhi)
 */
export default observer(function CodeEditor (props: CodeEditorProps) {
	const [formattedHtml, setFormattedHtml] = useState<string>("");
	useEffect(() => {
		const runFormatting = async () => {
			if (props.editorMode === "HTML") {
				const rawHtml = serializationManager.fromBlocksToHTML(contentManager.getDesignContent());
				console.log(rawHtml);
				if (contentManager.getHtmlContent().trim() === "") {
					contentManager.setHtmlContent(rawHtml);
				}
				const formatted = await prettier.format(rawHtml, {
					parser: "html",
					plugins: [parserHtml],
					tabWidth: 4,
					useTabs: false,
				});
				setFormattedHtml(formatted);
				Prism.highlightAll();
			}
			// 🆕 STYLES (CSS) MODE
			else if (props.editorMode === "Styles") {
				// Get your MobX state for styles:
				// (replace with the correct import or access, e.g. pageBuilderStore.bodyStylesState)
				const bodyStylesState = bodyStylesManager.getBodyStylesState();
				const cssContent = [
					...serializationManager.fromStylesToCssContent(bodyStylesManager.getBodyStylesState()),
					...serializationManager.serializeAllBlockCssContent(contentManager.getDesignContent())
				];
				console.log("CssContent[]:", JSON.stringify(contentManager.getCssContent(), null, 2));

				const rawCss = cssContent.map(entry => entry.css).join('\n\n');

				// Format CSS with Prettier
				const formatted = await prettier.format(rawCss, {
					parser: "css",
					plugins: [parserCss],
					tabWidth: 4,
					useTabs: false,
				});
				setFormattedHtml(formatted);
				Prism.highlightAll();
			}
		};

		runFormatting();
	}, [
		props.editorMode,
		contentManager.getDesignContent(), 
		// Add bodyStylesState as a dependency so it updates on style changes
	]);

	return (
		<div className="relative flex flex-col w-full h-[800px] bg-white">
			<div 
				className="border border-gray-300 w-full h-full flex items-start justify-start z-20"
				style={{
					backgroundColor: "#f5f2f0"
				}}
			>
				<pre contentEditable={true} className="m-0 outline-none focus:outline-none whitespace-pre-wrap px-4 overflow-y-auto text-wrap text-base font-mono text-gray-800 h-full w-full">
				{props.editorMode === "HTML" && (
					<code className="language-markup">
						{formattedHtml}
					</code>
				)}
				{props.editorMode === "Styles" && (
					<code className="language-css">
						{formattedHtml}
					</code>
				)}
				{props.editorMode === "text" && (<span className="text-gray-400">Text Editing Mode (coming soon)</span>)}
				</pre>
			</div>
		</div>
	);
});
